import { useState, useEffect, useCallback } from 'react'
import { Plus, MoreVertical, ExternalLink, Copy, Settings, Trash2, Eye } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { useAuth } from '@/hooks/useAuth'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'

interface ProjectStats {
  totalSessions?: number
  activeSessions?: number
  endedSessions?: number
  totalMessages?: number
}

interface Project {
  id: string
  name: string
  description?: string
  domain?: string
  status: 'active' | 'paused' | 'archived'
  config?: any
  stats?: ProjectStats
  apiKeys?: Array<{ id: string; name: string; keyPrefix: string }>
  createdAt: string
  updatedAt?: string
}

export default function Projects() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newProject, setNewProject] = useState({ name: '', domain: '', description: '' })

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const res = await fetch(`${API_BASE_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (res.ok) {
        const data = await res.json()
        setProjects(data.projects || [])
      } else {
        const errData = await res.json().catch(() => ({}))
        setError(errData.message || 'Failed to load projects')
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err)
      setError('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user) {
      fetchProjects()
    }
  }, [user, fetchProjects])

  const handleCreateProject = async () => {
    if (!newProject.name) return

    try {
      setCreating(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const body: Record<string, string> = { name: newProject.name }
      if (newProject.description) body.description = newProject.description

      const res = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      if (res.ok) {
        setNewProject({ name: '', domain: '', description: '' })
        setShowCreateModal(false)
        await fetchProjects()
      } else {
        const errData = await res.json().catch(() => ({}))
        console.error('Failed to create project:', errData.message || res.statusText)
      }
    } catch (err) {
      console.error('Failed to create project:', err)
    } finally {
      setCreating(false)
    }
  }

  const copyApiKey = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const getDisplayDomain = (project: Project): string => {
    return project.domain || project.config?.domain || '-'
  }

  const getDisplayApiKey = (project: Project): string => {
    if (project.apiKeys && project.apiKeys.length > 0) {
      return project.apiKeys[0].keyPrefix
    }
    return `pk_...${project.id.slice(-6)}`
  }

  const getConversationsCount = (project: Project): number => {
    return project.stats?.totalSessions ?? 0
  }

  const mapStatus = (status: string): 'active' | 'paused' | 'archived' => {
    if (status === 'active') return 'active'
    if (status === 'paused') return 'paused'
    return 'archived'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('projects.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('projects.subtitle')}</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">
          <Plus className="h-4 w-4 mr-2" />
          {t('projects.newProject')}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={fetchProjects} className="mt-2 text-sm font-medium text-red-600 hover:text-red-800">
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          ))
        ) : projects.length === 0 && !error ? (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 mb-4">{t('projects.noProjects') || 'No projects yet'}</p>
            <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              {t('projects.createFirst') || 'Create your first project'}
            </button>
          </div>
        ) : (
          projects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{project.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {getDisplayDomain(project)}
                  </p>
                </div>
                <div className="relative">
                  <button className="p-1 text-gray-400 hover:text-gray-600"><MoreVertical className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{t('projects.status')}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${mapStatus(project.status) === 'active' ? 'bg-green-100 text-green-800' : mapStatus(project.status) === 'paused' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{project.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{t('projects.conversations')}</span>
                  <span className="text-sm font-medium text-gray-900">{getConversationsCount(project).toLocaleString()}</span>
                </div>
                {project.description && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 truncate">{project.description}</span>
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm text-gray-500">{t('projects.apiKey')}</label>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-600 truncate">{getDisplayApiKey(project)}</code>
                    <button onClick={() => copyApiKey(getDisplayApiKey(project))} className="p-1 text-gray-400 hover:text-gray-600" title={t('common.copy')}><Copy className="h-3 w-3" /></button>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex space-x-2">
                <button className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <Eye className="h-4 w-4 mr-1" />
                  {t('common.view')}
                </button>
                <button className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <Settings className="h-4 w-4 mr-1" />
                  {t('nav.settings')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCreateModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">{t('projects.createNewProject')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="project-name" className="block text-sm font-medium text-gray-700">{t('projects.projectName')}</label>
                      <input type="text" id="project-name" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="My Website" />
                    </div>
                    <div>
                      <label htmlFor="project-description" className="block text-sm font-medium text-gray-700">{t('projects.description') || 'Description'}</label>
                      <input type="text" id="project-description" value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Project description (optional)" />
                    </div>
                    <div>
                      <label htmlFor="project-domain" className="block text-sm font-medium text-gray-700">{t('projects.domain')}</label>
                      <input type="text" id="project-domain" value={newProject.domain} onChange={(e) => setNewProject({ ...newProject, domain: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="mywebsite.com" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse space-y-3 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
                <button type="button" onClick={handleCreateProject} disabled={creating || !newProject.name} className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto">{creating ? (t('common.creating') || 'Creating...') : t('projects.createProject')}</button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
